import {property} from '@wonderlandengine/api/decorators.js';
import {Align, Justify, ReactUiBase} from '@wonderlandengine/react-ui';
import {
    Button,
    Column,
    MaterialContext,
    Panel,
    Text,
} from '@wonderlandengine/react-ui/components';

import {CustomProgressBar} from './custom-components.js';
import {Object3D} from '@wonderlandengine/api';
import React, {useState, useEffect, useRef} from 'react';
import profile, {getLevelForXP, getXPPercentToNext} from './utils/profile.js';
import {delay, play, updateAnimations} from './utils/animation-context.js';
import {AudioManager, globalAudioManager} from '@wonderlandengine/spatial-audio';

import type {GameAnalytics as GA} from 'gameanalytics';
import {ProgressionStatus} from './game-analytics-enums.js';
declare var GameAnalytics: typeof GA;

enum Sounds {
    LevelUp,
}

export const App = (props: {comp: RewardScreenUi; onClose: () => void}) => {
    const comp = props.comp;

    /* XP will be updated right after this call, so store the original value */
    const [gain, setGain] = useState(0);

    const [percent, setPercent] = useState(getXPPercentToNext(profile.getXP()));
    const [level, setLevel] = useState(profile.getLevel());

    useEffect(() => {
        props.comp.setGain = setGain;
    }, [setGain]);

    const runAnimations = async () => {
        const p = getXPPercentToNext(profile.getXP());
        const xp = profile.getXP();
        const nextLevel = profile.getLevel() + 1;
        const levelAfterGain = getLevelForXP(xp + gain);
        setPercent(p);

        await delay(1);

        const from = getXPPercentToNext(xp);
        const to = getXPPercentToNext(xp + gain);

        if (levelAfterGain >= nextLevel) {
            await play((f) => setPercent(from * (1 - f) + f * 1.0), 0.75);

            setLevel(nextLevel);
            (globalAudioManager as AudioManager).play(Sounds.LevelUp, {
                volume: 0.5,
            });

            await play((f) => setPercent(f * to), 0.75);

            GameAnalytics.addProgressionEvent(
                ProgressionStatus.Complete,
                'level-up',
                nextLevel
            );
        } else {
            await play((f) => setPercent(from * (1 - f) + f * to), 0.75);
        }
        await delay(7);
    };

    useEffect(() => {
        runAnimations();
    }, [gain]);

    return (
        <MaterialContext.Provider value={props.comp}>
            <Panel
                // material={props.comp.panelMaterial}
                backgroundColor={comp.panel}
                resolution={100}
                rounding={80}
                width="100%"
                height="100%"
                justifyContent={Justify.Center}
            >
                <Column>
                    <Text
                        fontSize={130}
                        alignSelf={Align.Center}
                        textAlign="center"
                        marginBottom={20}
                    >
                        {`Lvl. ${level}`}
                    </Text>
                    <CustomProgressBar
                        alignSelf={Align.Center}
                        value={percent}
                        width="80%"
                        height={90}
                        bgColor={comp.bg}
                        fgColor={comp.fg}
                        marginBottom={100}
                    ></CustomProgressBar>
                    <Text fontSize={50} alignSelf={Align.Center} textAlign="center">
                        You gained
                    </Text>
                    <Text fontSize={80} alignSelf={Align.Center} textAlign="center">
                        {`${gain} xp.`}
                    </Text>
                </Column>
            </Panel>
        </MaterialContext.Provider>
    );
};

export class RewardScreenUi extends ReactUiBase {
    static TypeName = 'reward-screen-ui';
    static InheritProperties = true;

    @property.color(1, 1, 1, 1)
    panel!: Float32Array;

    @property.color(1, 1, 1, 1)
    bg!: Float32Array;

    @property.color(1, 1, 1, 1)
    fg!: Float32Array;

    @property.object()
    rewardScreenobj!: Object3D;

    setGain?: (gain: number) => void;

    value: number = 0;

    async start() {
        globalAudioManager.load('sfx/level-up.mp3', Sounds.LevelUp);
        await super.start();

        // Initialize rewardUi state to be inactive at start
        this.active = false;
    }

    onActivate(): void {
        this.setActiveRecursively(this.object, true);
    }

    onDeactivate(): void {
        this.setActiveRecursively(this.object, false);
    }

    render() {
        return <App comp={this} onClose={() => (this.active = false)} />;
    }

    public async gainXP(gain: number) {
        this.active = true;

        /* We need to render one frame for setGain to be set */
        setTimeout(() => {
            if (this.setGain) this.setGain(gain);
        }, 1);

        await delay(10);
        this.active = false;
    }

    override update(dt: number) {
        super.update();
        updateAnimations(dt);
    }

    setActiveRecursively(o: Object3D, active: boolean) {
        const comps = o.getComponents();
        comps.forEach((c) => {
            if (c == this) return;
            c.active = active;
        });
        o.children.forEach((c) => {
            this.setActiveRecursively(c, active);
        });
    }
}
